import axios, { AxiosHeaders } from "axios";
import {
  clearAdminTokens,
  getAdminAccessToken,
  getAdminRefreshToken,
  setAdminTokens,
} from "./adminSession";
import {
  clearUserTokens,
  getUserAccessToken,
  getUserRefreshToken,
  setUserTokens,
} from "./userSession";

import { resolveApiBase } from "./apiBase";

/**
 * Local dev: same-origin /api (setupProxy → REACT_APP_PROXY_TARGET).
 * Production: https://api.coinmbx.com (REACT_APP_BACKEND_URL in .env.production).
 */
export const api = axios.create({
  baseURL: resolveApiBase(),
  withCredentials: true,
});

function isAdminApi(url) {
  const u = url || "";
  return (
    u.includes("/api/admin/") ||
    u.includes("/api/admin-auth/") ||
    u.includes("/api/admin/security/")
  );
}

api.interceptors.request.use((config) => {
  config.baseURL = resolveApiBase();
  const headers = AxiosHeaders.from(config.headers);

  // FormData: let axios/browser set multipart boundary (never application/json).
  if (config.data instanceof FormData) {
    headers.delete("Content-Type");
  } else if (
    config.data != null &&
    typeof config.data === "object" &&
    !(config.data instanceof Blob) &&
    !(config.data instanceof ArrayBuffer)
  ) {
    if (!headers.hasContentType()) {
      headers.setContentType("application/json");
    }
  }

  if (isAdminApi(config.url)) {
    const access = getAdminAccessToken();
    if (access) {
      headers.set("X-Admin-Authorization", `Bearer ${access}`);
    }
    const refresh = getAdminRefreshToken();
    if (refresh && (config.url || "").includes("/api/admin-auth/refresh")) {
      headers.set("X-Admin-Refresh-Token", refresh);
    }
  } else {
    const access = getUserAccessToken();
    if (access) {
      headers.set("Authorization", `Bearer ${access}`);
    }
    const refresh = getUserRefreshToken();
    if (refresh && (config.url || "").includes("/api/auth/refresh")) {
      headers.set("Cookie", `refresh_token=${refresh}`);
    }
  }

  config.headers = headers;
  return config;
});

const USER_AUTH_TOKEN_PATHS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
];

api.interceptors.response.use((response) => {
  const url = response.config?.url || "";
  if (USER_AUTH_TOKEN_PATHS.some((p) => url.includes(p))) {
    const { access_token, refresh_token } = response.data || {};
    if (access_token) setUserTokens(access_token, refresh_token);
  }
  if (url.includes("/api/admin-auth/login") || url.includes("/api/admin-auth/refresh")) {
    const { admin_access_token, admin_refresh_token } = response.data || {};
    if (admin_access_token) setAdminTokens(admin_access_token, admin_refresh_token);
  }
  return response;
});

/** Base URL for exports / WebSockets / blog media (deploy API or dev proxy origin). */
export function apiBaseUrl() {
  const base = resolveApiBase();
  if (base) return base;
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

/** ws:// / wss:// base aligned with `api` (dev proxy → localhost:3000, not VPS .env). */
export function wsBaseUrl() {
  const http = apiBaseUrl();
  if (!http) return "";
  return http.replace(/^http/, "ws");
}

/**
 * Auto-refresh on 401: if the access_token cookie has expired/been deleted by
 * the browser (~60 min idle), POST /api/auth/refresh once using the still-valid
 * refresh_token cookie (7-day TTL), then retry the original request.
 *
 * Skip-list = endpoints that MUST NOT trigger a refresh attempt:
 *   - /auth/refresh itself (would create an infinite loop)
 *   - /auth/login + /auth/register (no session yet)
 *   - /auth/forgot-password + /auth/reset-password (public)
 * Everything else, including /auth/me, gets a single retry after refresh.
 */
const SKIP_USER_REFRESH = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
];

const SKIP_ADMIN_REFRESH = [
  "/api/admin-auth/login",
  "/api/admin-auth/refresh",
  "/api/admin-auth/logout",
];

let userRefreshInflight = null;
let adminRefreshInflight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";

    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }

    const adminRoute = isAdminApi(url);
    const skipList = adminRoute ? SKIP_ADMIN_REFRESH : SKIP_USER_REFRESH;
    if (skipList.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      if (adminRoute) {
        adminRefreshInflight =
          adminRefreshInflight ||
          api.post("/api/admin-auth/refresh").finally(() => {
            adminRefreshInflight = null;
          });
        const { data } = await adminRefreshInflight;
        if (data?.admin_access_token) {
          setAdminTokens(data.admin_access_token, data.admin_refresh_token);
        }
        if (original.data instanceof FormData) {
          const retryHeaders = AxiosHeaders.from(original.headers);
          retryHeaders.delete("Content-Type");
          original.headers = retryHeaders;
        }
      } else {
        const userRefresh = getUserRefreshToken();
        userRefreshInflight =
          userRefreshInflight ||
          api
            .post(
              "/api/auth/refresh",
              {},
              userRefresh
                ? { headers: { Cookie: `refresh_token=${userRefresh}` } }
                : undefined,
            )
            .finally(() => {
              userRefreshInflight = null;
            });
        await userRefreshInflight;
      }
      return api(original);
    } catch {
      if (adminRoute) clearAdminTokens();
      else clearUserTokens();
      return Promise.reject(error);
    }
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => {
        if (!e || typeof e !== "object") return String(e);
        const loc = Array.isArray(e.loc)
          ? e.loc.filter((p) => p !== "body").join(".")
          : "";
        const msg = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" • ");
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
  }
  return String(detail);
}

export function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  return formatApiErrorDetail(detail) || err?.message || "Request failed";
}
